import { Box, Typography, Chip, Stack } from "@mui/joy";
import ReactECharts from "echarts-for-react";
import { useDashboardData } from "../hooks";

export default function ProjectDeadlines() {
    const { deadlineStats } = useDashboardData();

    const deadlineItems = [
        {
            label: "Complete before deadline",
            count: deadlineStats.beforeDeadline,
            trend: deadlineStats.beforeTrend,
            prevCount: deadlineStats.prevBeforeDeadline,
            hex: "#10b981",
        },
        {
            label: "Complete on deadline",
            count: deadlineStats.onDeadline,
            trend: deadlineStats.onTrend,
            prevCount: deadlineStats.prevOnDeadline,
            hex: "#3b82f6",
        },
        {
            label: "Complete after deadline",
            count: deadlineStats.afterDeadline,
            trend: deadlineStats.afterTrend,
            prevCount: deadlineStats.prevAfterDeadline,
            hex: "#f59e0b",
        },
    ];

    const totalTasks = deadlineStats.total;

    const option = {
        textStyle: { fontFamily: "Albert Sans, sans-serif" },
        // 3 independent polar systems = 3 well-spaced concentric rings
        polar: [
            { center: ["50%", "50%"], radius: ["80%", "92%"] },
            { center: ["50%", "50%"], radius: ["62%", "74%"] },
            { center: ["50%", "50%"], radius: ["44%", "56%"] },
        ],
        angleAxis: deadlineItems.map((_, i) => ({
            polarIndex: i,
            max: Math.max(totalTasks * 1.2, 1),
            startAngle: 90,
            show: false,
        })),
        radiusAxis: deadlineItems.map((_, i) => ({
            polarIndex: i,
            type: "category",
            show: false,
        })),
        series: deadlineItems.map((stat, i) => ({
            type: "bar",
            data: [stat.count],
            coordinateSystem: "polar",
            polarIndex: i,
            name: stat.label,
            roundCap: true,
            showBackground: true,
            backgroundStyle: {
                color: "rgba(0, 0, 0, 0.05)",
            },
            itemStyle: {
                color: stat.hex,
            },
        })),
        tooltip: {},
        graphic: [
            {
                type: "text",
                left: "center",
                top: "middle",
                style: {
                    text: totalTasks.toString(),
                    textAlign: "center",
                    fill: "#1f2937",
                    fontSize: 32,
                    fontWeight: "bold",
                },
            },
        ],
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
                <Typography level="title-lg">Project deadlines</Typography>
                <Typography level="body-md">Status of completion for all tasks</Typography>
            </Box>

            <Box sx={{ height: 180, position: "relative", my: -2 }}>
                <ReactECharts option={option} style={{ height: "100%", width: "100%" }} opts={{ renderer: "svg" }} />
            </Box>

            <Stack spacing={1.5}>
                {deadlineItems.map((stat, i) => (
                    <Box
                        key={i}
                        sx={{
                            p: 2,
                            borderRadius: "lg",
                            bgcolor: "background.level1",
                            display: "flex",
                            gap: 1.5,
                            px: 3,
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: 4,
                                height: "2.5rem",
                                borderRadius: "sm",
                                bgcolor: stat.hex,
                                flexShrink: 0,
                            }}
                        />
                        <Box>
                            <Typography level="title-md">
                                {stat.label}: {stat.count}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    variant="soft"
                                    color={
                                        stat.trend.startsWith("+")
                                            ? "success"
                                            : stat.trend.startsWith("-")
                                              ? "danger"
                                              : "neutral"
                                    }
                                    sx={{
                                        minHeight: "18px",
                                        borderRadius: "xs",
                                        px: 0.6,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {stat.trend}
                                </Chip>
                                <Typography level="body-md" sx={{ opacity: 0.7 }}>
                                    than previous {stat.prevCount} projects
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
